import { createContext, useCallback, useContext, useEffect, useMemo, useState, type MouseEvent, type ReactNode } from "react";

interface CredentialRevealGuardValue {
  active: boolean;
  confirmed: boolean;
  canLeave: boolean;
  beginReveal: () => void;
  setConfirmed: (confirmed: boolean) => void;
  clearReveal: () => void;
  guardNavigation: (event: MouseEvent<HTMLElement>) => void;
  notifyBlocked: () => void;
  blockedAttempt: boolean;
}

const CredentialRevealGuardContext = createContext<CredentialRevealGuardValue | null>(null);

export function CredentialRevealGuardProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [blockedAttempt, setBlockedAttempt] = useState(false);

  const beginReveal = useCallback(() => {
    setActive(true);
    setConfirmed(false);
    setBlockedAttempt(false);
  }, []);

  const clearReveal = useCallback(() => {
    setActive(false);
    setConfirmed(false);
    setBlockedAttempt(false);
  }, []);

  const guardNavigation = useCallback((event: MouseEvent<HTMLElement>) => {
    if (!active || confirmed) return;
    event.preventDefault();
    setBlockedAttempt(true);
  }, [active, confirmed]);

  const notifyBlocked = useCallback(() => {
    if (!active || confirmed) return;
    setBlockedAttempt(true);
  }, [active, confirmed]);

  useEffect(() => {
    if (!active || confirmed) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [active, confirmed]);

  const value = useMemo<CredentialRevealGuardValue>(
    () => ({
      active,
      confirmed,
      canLeave: !active || confirmed,
      beginReveal,
      setConfirmed,
      clearReveal,
      guardNavigation,
      notifyBlocked,
      blockedAttempt,
    }),
    [active, confirmed, beginReveal, clearReveal, guardNavigation, notifyBlocked, blockedAttempt],
  );

  return <CredentialRevealGuardContext.Provider value={value}>{children}</CredentialRevealGuardContext.Provider>;
}

export function useCredentialRevealGuard(): CredentialRevealGuardValue {
  const context = useContext(CredentialRevealGuardContext);
  if (!context) throw new Error("useCredentialRevealGuard precisa estar dentro de <CredentialRevealGuardProvider />.");
  return context;
}
